import React, { ReactNode, useState } from 'react'
import { NavigateFn } from '@reach/router'
import {
  Button,
  Card,
  Flex,
  IconButton,
  PrettyDate,
  Confirmation,
  Toaster,
  PrettyNumber,
  EmployeeWithDetailsPopover,
  Popover,
  Icon,
  FilterContainer,
  FilterButton,
  MultiLocationSelect,
  MultiEmployeeSelect,
  ColorSquare,
  GridTableStatusEnum,
  NewGridTable,
  IGridTableColumn,
  IGridTableCell,
} from '@ui'
import numeral from 'numeral'

import { UseNeighborhoodsHook } from './hooks/useNeighborhoods'
import api, { INeighborhoodTableRecord, Role } from '@api'
import { pluralize } from '@utils'
import { Classes, Intent, Menu, MenuItem, Collapse, ProgressBar, Position } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { ISelection } from '@hooks/useSelection'
import { ApolloError } from 'apollo-boost'
import { useReload } from '@hooks/reloadContext'
import { Permissions, PermissionsType, IPermissionsOnNeighborhood } from '@permissions'
import track from '@track'
import Event from '@track/Event'
import classnames from 'classnames'
import { useUser } from '@hooks'

interface TableViewProps {
  neighborhoods: UseNeighborhoodsHook
  navigate?: NavigateFn
}

export default function TableView(props: TableViewProps) {
  const { neighborhoods, navigate } = props
  const { user } = useUser()
  const [showFilters, setShowFilters] = useState(true)
  const excludeColumns = ['occupancy', 'sharingRatio', 'capacity']

  let columns: IGridTableColumn<INeighborhoodTableRecord>[] = [
    {
      label: 'Name',
      sort: 'name',
      tooltip: <>The name of the neighborhood in the system.</>,
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>name of neighborhood</span>

        const neighborhood = (cell as IGridTableCell<INeighborhoodTableRecord>).data

        return (
          <div className="flex items-center">
            <ColorSquare style={{ background: `#${neighborhood.color}` }} />
            <div
              onClick={() => {
                navigate?.(`?neighborhoodId=${neighborhood.id}`)
                track.neighborhoods({ eventName: Event.ROW_CLICK })
              }}
              className={classnames(
                'ml-2 font-medium text-base hover:underline truncate cursor-pointer'
              )}>
              {neighborhood.name}
            </div>
          </div>
        )
      },
    },
    {
      label: 'Location',
      sort: 'location',
      tooltip: <>The floor, building and city where this neighborhood exists.</>,
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>building</span>

        const neighborhood = (cell as IGridTableCell<INeighborhoodTableRecord>).data

        return (
          <Flex direction="column">
            <div>
              {neighborhood.floor.name}, {neighborhood.floor.building.name}
            </div>
            <div style={{ color: 'var(--grey1)' }}>{neighborhood.floor.building.city}</div>
          </Flex>
        )
      },
    },
    {
      label: 'Employees Associated',
      alignRight: true,
      sort: 'employeesAssociated',
      noWrapHeader: true,
      tooltip: (
        <>
          The employees count in this neighborhood. Can be added manually or by adding/removing
          departments.
        </>
      ),
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>employees</span>

        const neighborhood = (cell as IGridTableCell<INeighborhoodTableRecord>).data

        return (
          <PrettyNumber
            value={neighborhood.counters.employees}
            data-input={`${neighborhood.name}_employees_associated`}
          />
        )
      },
    },
    {
      label: 'Spaces Associated',
      alignRight: true,
      noWrapHeader: true,
      tooltip: (
        <>
          The physical count of seats assigned to this neighborhood. Can be less than the number of
          employees.
        </>
      ),
      sort: 'spacesAssociated',
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>spaces</span>

        const neighborhood = (cell as IGridTableCell<INeighborhoodTableRecord>).data

        return (
          <PrettyNumber
            value={neighborhood.counters.workstations}
            data-input={`${neighborhood.name}_seats_associated`}
          />
        )
      },
    },
    {
      label: 'Sharing ratio',
      alignRight: true,
      sort: 'sharingRatio',
      noWrapHeader: true,
      tooltip: (
        <>The number of employees divided by the number of seats. Can be greater than 1.0.</>
      ),
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>sharing r</span>

        const neighborhood = (cell as IGridTableCell<INeighborhoodTableRecord>).data

        return <div>{Number(neighborhood.counters.sharingRatio.toFixed(2))}</div>
      },
    },
    {
      label: 'Capacity',
      alignRight: true,
      sort: 'capacity',
      tooltip: (
        <>
          The maximum number of employees allowed in the neighborhood. Can be greater than the
          number of seats.
        </>
      ),
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>capacity</span>

        const neighborhood = (cell as IGridTableCell<INeighborhoodTableRecord>).data

        const alert = Number(neighborhood.counters.maxEmployees) < neighborhood.counters.employees

        return (
          <Tooltip2 content="Capacity less than amount of employees" disabled={!alert}>
            <div
              style={{
                color: alert ? 'red' : '',
              }}>
              {neighborhood.counters.maxEmployees}
            </div>
          </Tooltip2>
        )
      },
    },
    {
      label: 'Occupancy %',
      sort: 'occupancy',
      cellStyle: {
        minWidth: 180,
      },
      tooltip: (
        <>The number of employees divided by the capacity. The virtual or effective occupancy.</>
      ),
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>occupancy</span>

        const neighborhood = (cell as IGridTableCell<INeighborhoodTableRecord>).data

        return (
          <div className="w-full">
            <PercentBar value={neighborhood.counters.occupancy} />
          </div>
        )
      },
    },
    {
      label: 'Created By/Date',
      sort: 'createdAt',
      noWrapHeader: true,
      tooltip: <>The employee who created the neighborhood.</>,
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>name of neighborhood</span>

        const neighborhood = (cell as IGridTableCell<INeighborhoodTableRecord>).data

        return (
          <Flex style={{ color: 'var(--grey1)' }} align="center">
            {neighborhood.createdBy && (
              <>
                <EmployeeWithDetailsPopover
                  buttonProps={{ small: true, minimal: true }}
                  employee={neighborhood.createdBy.employee}
                />
                <span style={{ paddingRight: '0.4em', fontSize: 17 }}>•</span>
              </>
            )}
            <div style={{ paddingLeft: neighborhood.createdBy ? 0 : '7px' }}>
              <PrettyDate value={neighborhood.createdAt} />
            </div>
          </Flex>
        )
      },
    },
  ]

  const format = (items: INeighborhoodTableRecord[]) =>
    items.map((i) => ({
      data: i,
    }))

  const formated = format(neighborhoods.items)

  if (user?.employee.role === Role.DEPARTMENT_ADMIN) {
    columns = columns.filter((c) => !excludeColumns.includes(c.sort as string))
  }

  return (
    <>
      <Card.Header>
        <Toolbar
          {...{
            navigate,
            neighborhoods,
            showFilters,
            setShowFilters,
          }}
        />
      </Card.Header>
      <NewGridTable<INeighborhoodTableRecord>
        status={
          neighborhoods.isLoading
            ? GridTableStatusEnum.LOADING
            : neighborhoods.isPaging
            ? GridTableStatusEnum.PAGING
            : neighborhoods.isSorting
            ? GridTableStatusEnum.SORTING
            : GridTableStatusEnum.IDLE
        }
        minCellWidth="max-content"
        columns={columns}
        data={formated}
        sticky={true}
        selection={neighborhoods.selection}
        sort={{
          column: neighborhoods.sort.column,
          order: neighborhoods.sort.order,
          onSort: (column) => {
            neighborhoods.selection.clear()
            neighborhoods.sortBy(column)
            track.neighborhoods({
              eventName: Event.SORT,
              props: { columnName: column },
            })
          },
        }}
        noDataMessage={
          neighborhoods.filterCount()
            ? undefined
            : 'You haven\'t created any neighborhoods yet. To add a neighborhood, select the "+ Neighborhood" button at top right, or go to a floor map and add a neighborhood there.'
        }
        page={{
          hasNextPage: neighborhoods.pageInfo.hasNextPage,
          endCursor: neighborhoods.pageInfo.endCursor,
          onNextPage: () => {
            neighborhoods.nextPage()
            track.neighborhoods({
              eventName: Event.PAGE,
            })
          },
        }}
        track={{
          select: () =>
            track.neighborhoods({
              eventName: Event.ROW_SELECT,
            }),
          selectAll: () =>
            track.neighborhoods({
              eventName: Event.SELECT_ALL,
            }),
        }}
        headerTop={showFilters ? 183 : 72}
      />
    </>
  )
}

const placeholder = <span className={Classes.SKELETON}>placeholder</span>

function Toolbar({
  neighborhoods,
  navigate,
  showFilters,
  setShowFilters,
}: {
  neighborhoods: UseNeighborhoodsHook
  navigate?: NavigateFn
  showFilters: boolean
  setShowFilters: (showFilters: boolean) => void
}) {
  let helperText = !neighborhoods.isLoading
    ? `${neighborhoods.count} ${pluralize(neighborhoods.count, 'record')}`
    : placeholder

  let clearButton: ReactNode = null
  const selectedNeighborhoods = neighborhoods.items.filter(
    (e) => neighborhoods.selection.items[e.id]
  )

  if (!neighborhoods.isLoading) {
    if (neighborhoods.selection.count) {
      helperText = `${neighborhoods.selection.count} selected`
      clearButton = (
        <IconButton
          minimal
          tooltip="Clear selection"
          iconName="times"
          style={{ marginLeft: 4 }}
          data-button="clear_selection"
          onClick={() => {
            neighborhoods.selection.clear()
            track.neighborhoods({
              eventName: Event.CLEAR_SELECTION,
            })
          }}
        />
      )
    } else if (neighborhoods.filterCount()) {
      helperText = `${numeral(neighborhoods.count).format('0,0')} filtered record${
        neighborhoods.count === 1 ? '' : 's'
      }`

      clearButton = (
        <IconButton
          minimal
          tooltip="Clear filter"
          iconName="times"
          style={{ marginLeft: 4 }}
          onClick={() => {
            neighborhoods.clearFilters()
            track.neighborhoods({
              eventName: Event.CLEAR_ALL_FILTERS,
            })
          }}
        />
      )
    }
  }

  return (
    <Flex direction="column" style={{ marginLeft: -24, width: '98%', background: 'var(--white)' }}>
      <Card.Header style={{ paddingLeft: 24 }}>
        <Flex align="baseline" style={{ marginLeft: 10 }}>
          {neighborhoods.selection.count ? (
            <Permissions
              type={PermissionsType.NEIGHBORHOOD}
              on={neighborhoods.items.filter(({ id }) =>
                Object.keys(neighborhoods.selection.items).includes(id)
              )}>
              {(permissions) => (
                <>
                  <MultiDeleteButton
                    disabled={!permissions.canDelete}
                    selectedNeighborhoods={selectedNeighborhoods}
                    selection={neighborhoods.selection}
                    onConfirm={() => {
                      neighborhoods.selection.clear()
                      neighborhoods.optimisticDelete(Object.keys(neighborhoods.selection.items))
                    }}
                  />
                  <div style={{ marginLeft: 8 }} />
                </>
              )}
            </Permissions>
          ) : (
            <FilterButton
              onClick={() => setShowFilters(!showFilters)}
              filterCount={neighborhoods.filterCount()}
            />
          )}
          <Flex align="baseline" style={{ marginLeft: 10 }}>
            {helperText}
            {clearButton}
          </Flex>
        </Flex>
        <Flex>
          <Button
            data-button="new_neighborhood"
            iconName="plus"
            text="New Neighborhood"
            onClick={() => {
              navigate?.('?newItem=neighborhood')
              track.neighborhoods({
                eventName: Event.NEW_RECORD_DIALOG,
              })
            }}
          />
          <Permissions type={PermissionsType.NEIGHBORHOOD}>
            {(permissions) => {
              const { canOpenReport } = permissions as IPermissionsOnNeighborhood
              const AGILE_WORKPLACE = 'Agile Workplace'
              return canOpenReport ? (
                <Popover
                  position={Position.BOTTOM}
                  content={
                    <Menu>
                      <MenuItem
                        text="Neighborhoods Overview Report"
                        onClick={() => {
                          navigate?.(
                            `/reports/${encodeURIComponent(AGILE_WORKPLACE)}/NeighborhoodsReport`,
                            {}
                          )
                        }}
                        icon={<Icon style={{ marginTop: 2, width: 22 }} name="chart-bar" />}
                      />
                      <MenuItem
                        text="Import"
                        onClick={() => {
                          navigate?.('?newItem=neighborhoodsImport')
                        }}
                        icon={<Icon style={{ marginTop: 2, width: 22 }} name="cloud-upload" />}
                      />
                      <MenuItem
                        text="Import History"
                        onClick={() => navigate?.('/neighborhoods/history')}
                        icon={<Icon style={{ marginTop: 2, width: 22 }} name="history" />}
                      />
                      {/* <MenuItem
            text="Download CSV"
            icon={
              <Icon style={{ marginTop: 2, width: 22 }} name="download" />
            }
            onClick={() =>
              fetchExportLink().then((url) => startFileDownload(url))
            }
          /> */}
                    </Menu>
                  }>
                  <IconButton
                    round={false}
                    className="ml2"
                    iconName="ellipsis-h"
                    tooltip="More actions"
                    data-button="more_actions"
                  />
                </Popover>
              ) : null
            }}
          </Permissions>
        </Flex>
      </Card.Header>
      <Collapse isOpen={showFilters} keepChildrenMounted>
        <Flex style={{ marginBottom: 10, paddingLeft: 24 }}>
          <FilterContainer>
            <MultiLocationSelect
              mode={MultiLocationSelect.Mode.MULTI_FLOOR}
              values={neighborhoods.filters.locations}
              onChange={(newSelection) => neighborhoods.setFilteredLocations(newSelection)}
            />
          </FilterContainer>
          <FilterContainer>
            <MultiEmployeeSelect
              values={neighborhoods.filters.createdBy}
              onChange={(items) => {
                neighborhoods.setCreatedBy(items)
              }}
              title="Created By"
            />
          </FilterContainer>
        </Flex>
      </Collapse>
    </Flex>
  )
}

function MultiDeleteButton({
  disabled,
  selectedNeighborhoods,
  selection,
  onConfirm,
}: {
  disabled?: boolean
  selectedNeighborhoods: INeighborhoodTableRecord[]
  selection: ISelection
  onConfirm?: () => void
}) {
  const reload = useReload()

  return (
    <Confirmation
      disabled={disabled}
      text={`Delete selected ${pluralize(selectedNeighborhoods.length, 'neighborhood')}?`}
      onConfirm={() => {
        onConfirm?.()
        Promise.all(selectedNeighborhoods.map((e) => api.neighborhood.remove(e.id)))
          .then(() => {
            selection.clear()
            reload.neighborhoods()
            Toaster.success({
              message: `${selectedNeighborhoods.length} ${pluralize(
                selectedNeighborhoods.length,
                'neighborhood'
              )} deleted`,
            })

            track.neighborhoods({
              eventName: Event.BATCH_ACTION,
              props: {
                actionName: 'Remove neighborhoods',
                actionValue: null,
                batchCount: selectedNeighborhoods.length,
              },
            })
          })
          .catch((error: ApolloError) => {
            Toaster.fail({
              error,
            })
          })
      }}>
      <Button
        ghost
        data-button="delete_neighborhoods"
        disabled={disabled}
        intent={Intent.DANGER}
        iconName="trash"
        text="Delete"
      />
    </Confirmation>
  )
}

function PercentBar({ value, normal, alert }: { value: number; normal?: number; alert?: number }) {
  let intent: Intent | undefined
  if (normal && value >= normal) intent = Intent.WARNING
  if (alert && value >= alert) intent = Intent.DANGER
  return (
    <div className="f f10">
      <div className="f10 mr2 mt1">
        <ProgressBar animate={false} intent={intent} stripes={false} value={value / 100} />
      </div>
      <div style={{ minWidth: 35 }}>{`${value}%`}</div>
    </div>
  )
}
